/**
 * 创作人社媒跳转（2026-09-23 FR-03 增补，02 模块《社交媒体跳转按钮_实现说明》§5）。
 * - 介绍区 icon 横排：icon 恒为内置平台 SVG（mask 方式渲染以继承 currentColor），热区 ≥40px。
 * - 点击分流：移动端（pointer: coarse）一律 <a target=_blank> 直跳；
 *   桌面端有二维码 → <dialog> 社交卡（大图 + 图下全部平台跳转 icon 行）；
 *   桌面端无二维码 → 直接跳转。
 * - 焦点圈定/回归/Esc/背景锁滚动走原生 <dialog> showModal + TownPage 同款 body 锁。
 */
import { useEffect, useRef, useState } from 'react';
import type { Locale, SocialLink } from '@chronospeak/contracts';

import wechatIcon from './icons/wechat.svg';
import xiaohongshuIcon from './icons/xiaohongshu.svg';
import shipinhaoIcon from './icons/shipinhao.svg';
import douyinIcon from './icons/douyin.svg';
import bilibiliIcon from './icons/bilibili.svg';
import otherIcon from './icons/other.svg';

/**
 * 平台 icon → SVG URL。2026-09-23 晚：五枚主平台 icon 换用户提供品牌色实心版
 * （assets/icon/，iconfont 导出 1024 viewBox）——渲染改 <img>（品牌色，不走 mask 单色）；
 * bilibili 源文件无品牌色 → fill=currentColor 处理后仍归 <img> 形态但随文字色；
 * other 无品牌 icon，沿用站点单色外链 SVG（mask currentColor 与站点描边风格一致）。
 */
const PLATFORM_ICONS: Record<string, string> = {
  wechat: wechatIcon,
  xiaohongshu: xiaohongshuIcon,
  shipinhao: shipinhaoIcon,
  douyin: douyinIcon,
  bilibili: bilibiliIcon,
  other: otherIcon,
};

/** 品牌色 icon 平台（<img> 原色渲染）；bilibili/other 走 mask 单色继承 currentColor */
const BRAND_COLOR_ICONS = new Set(['wechat', 'xiaohongshu', 'shipinhao', 'douyin']);

/** 平台中文名（aria-label 用；label 缺省回退） */
const PLATFORM_NAMES_ZH: Record<string, string> = {
  wechat: '微信公众号',
  xiaohongshu: '小红书',
  shipinhao: '视频号',
  douyin: '抖音',
  bilibili: 'Bilibili',
  other: '主页',
};

const PLATFORM_NAMES_EN: Record<string, string> = {
  wechat: 'WeChat',
  xiaohongshu: 'Xiaohongshu',
  shipinhao: 'Channels',
  douyin: 'Douyin',
  bilibili: 'Bilibili',
  other: 'Homepage',
};

export function platformName(platform: string, locale: Locale): string {
  const table = locale === 'zh-CN' ? PLATFORM_NAMES_ZH : PLATFORM_NAMES_EN;
  return table[platform] ?? platform;
}

/** 弹窗标题（用户 2026-09-23 指示：「Ta的小红书」式，不只写名字）；en 用所有格 */
export function socialCardTitle(link: SocialLink, locale: Locale): string {
  return locale === 'zh-CN' ? `Ta的${platformName(link.platform, locale)}` : `Ta's ${platformName(link.platform, locale)}`;
}

/** 平台 icon 渲染（品牌色平台 <img> 原色；other mask 单色继承 currentColor） */
function PlatformIcon({ platform, className }: { platform: string; className: string }) {
  if (BRAND_COLOR_ICONS.has(platform)) {
    return <img className={className} src={PLATFORM_ICONS[platform]} alt='' aria-hidden='true' />;
  }
  const icon = PLATFORM_ICONS[platform] ?? PLATFORM_ICONS.other;
  return <span className={className} style={{ WebkitMaskImage: `url("${icon}")`, maskImage: `url("${icon}")` }} aria-hidden='true' />;
}

/** 社媒按钮（介绍区横排项；desktop 弹卡/直跳分流 + mobile 直跳由同一 a/button 分支承载） */
export function DialSocial({ links, name, locale }: { links: SocialLink[]; name: string; locale: Locale }) {
  const [cardLink, setCardLink] = useState<SocialLink | null>(null);
  const dialogRef = useRef<HTMLDialogElement | null>(null);
  const triggerRef = useRef<HTMLElement | null>(null);
  const [coarse, setCoarse] = useState(false);

  // 指针形态判定（02 实现说明 §5：pointer: coarse → 一律直跳；监听变化防折叠屏/外接鼠标）
  useEffect(() => {
    const mq = window.matchMedia('(pointer: coarse)');
    const apply = () => setCoarse(mq.matches);
    apply();
    mq.addEventListener('change', apply);
    return () => mq.removeEventListener('change', apply);
  }, []);

  // 社交卡 open/close：锁滚动 + 焦点回归（TownPage dialog 基线同款）
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    const onClose = () => {
      document.body.style.overflow = '';
      triggerRef.current?.focus();
    };
    dialog.addEventListener('close', onClose);
    return () => dialog.removeEventListener('close', onClose);
  }, [cardLink]);

  if (!links.length) return null;

  const openLink = (link: SocialLink) => {
    window.open(link.url, '_blank', 'noopener,noreferrer');
  };

  const onIconActivate = (link: SocialLink, el: HTMLElement) => {
    if (coarse) {
      openLink(link);
      return;
    }
    if (link.imageUrl) {
      triggerRef.current = el;
      setCardLink(link);
      requestAnimationFrame(() => dialogRef.current?.showModal());
      document.body.style.overflow = 'hidden';
    } else {
      openLink(link);
    }
  };

  return (
    <div className='dial-social'>
      {links.map((link) => {
        const label =
          locale === 'zh-CN' ? `${name}的${platformName(link.platform, locale)}` : `${name} on ${platformName(link.platform, locale)}`;
        // 移动端：语义化 <a> 直跳（新标签 + 不漏 referrer）；桌面端：button 承载弹卡/直跳分流
        return coarse ? (
          <a
            key={link.url}
            className='dial-social-btn'
            href={link.url}
            target='_blank'
            rel='noopener noreferrer'
            aria-label={label}
            title={label}
          >
            <PlatformIcon platform={link.platform} className='dial-social-icon' />
          </a>
        ) : (
          <button
            key={link.url}
            type='button'
            className='dial-social-btn'
            onClick={(e) => onIconActivate(link, e.currentTarget)}
            aria-label={label}
            title={label}
            aria-haspopup={link.imageUrl ? 'dialog' : undefined}
          >
            <PlatformIcon platform={link.platform} className='dial-social-icon' />
          </button>
        );
      })}

      {/* 社交卡（仅桌面有二维码时弹出）：二维码大图 + 图下全部平台跳转 icon 行 */}
      {cardLink && (
        <dialog
          className='dial-social-dialog'
          aria-label={locale === 'zh-CN' ? `${name}的社交媒体` : `${name}'s social media`}
          ref={dialogRef}
          onClick={(e) => {
            // 点击遮罩关闭（TownPage 同款：命中 dialog 自身且在内容区外）
            if (e.target === dialogRef.current) dialogRef.current?.close();
          }}
        >
          <button type='button' className='dial-social-close' data-close onClick={() => dialogRef.current?.close()} aria-label={locale === 'zh-CN' ? '关闭' : 'Close'}>
            ×
          </button>
          <p className='dial-social-dialog-name'>{socialCardTitle(cardLink, locale)}</p>
          {cardLink.imageUrl && (
            <img className='dial-social-qr' src={cardLink.imageUrl} alt={locale === 'zh-CN' ? `${platformName(cardLink.platform, locale)}名片二维码` : `${platformName(cardLink.platform, locale)} QR code`} />
          )}
          <p className='dial-social-dialog-hint'>
            <span
              className='dial-social-hint-icon'
              style={{ WebkitMaskImage: `url("${otherIcon}")`, maskImage: `url("${otherIcon}")` }}
              aria-hidden='true'
            />
            {locale === 'zh-CN' ? '扫码或点击下方图标前往' : 'Scan the code or tap an icon below'}
          </p>
          <div className='dial-social-row'>
            {links.map((link) => {
              const label = locale === 'zh-CN' ? `在${platformName(link.platform, locale)}打开${name}` : `Open ${name} on ${platformName(link.platform, locale)}`;
              return (
                <a
                  key={link.url}
                  className='dial-social-btn'
                  href={link.url}
                  target='_blank'
                  rel='noopener noreferrer'
                  aria-label={label}
                  title={label}
                >
                  <PlatformIcon platform={link.platform} className='dial-social-icon' />
                </a>
              );
            })}
          </div>
        </dialog>
      )}
    </div>
  );
}
